<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/user'
import { useAvatar } from '@/composables/useAvatar'
import { useAuth } from '@/composables/useAuth'
import { useLocale } from '@/composables/useLocale'
import { useTheme } from '@/composables/useTheme'

const { t } = useI18n()
import {
  UserOutlined,
  LogoutOutlined,
  GlobalOutlined,
  BgColorsOutlined,
  BulbOutlined,
  DesktopOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RightOutlined
} from '@ant-design/icons-vue'

const router = useRouter()
const route = useRoute()
defineProps<{ navigationExpanded?: boolean }>()
defineEmits<{ 'toggle-navigation': [] }>()
const userStore = useUserStore()
const { getAvatarUrl } = useAvatar()
const { logout } = useAuth()
const { currentLocale, localeName, setLocale, availableLocales, localeNames } = useLocale()
const { themeMode, actualTheme, setTheme } = useTheme()
const pageTitle = computed(() => {
  const title = String(route.meta.title || '')
  return title.startsWith('router.') ? t(title) : title
})

// 用户信息
const user = computed(() => userStore.userInfo || {})

// 头像URL
const avatarUrl = computed(() => getAvatarUrl(user.value.avatar))
// 主题图标
const themeIcon = computed(() => {
  switch (actualTheme.value) {
    case 'light':
      return BgColorsOutlined
    case 'dark':
      return BulbOutlined
    default:
      return DesktopOutlined
  }
})

// 主题文本
const themeText = computed(() => {
  switch (themeMode.value) {
    case 'light':
      return t('component.settings.theme.light')
    case 'dark':
      return t('component.settings.theme.dark')
    case 'auto':
      return t('component.settings.theme.auto')
    default:
      return t('component.settings.theme.title')
  }
})

/**
 * 退出登录
 */
async function handleLogout() {
  await logout()
}

/**
 * 跳转到个人中心
 */
function goToProfile() {
  router.push('/setting/profile')
}

/**
 * 切换语言
 */
function handleLocaleChange(locale: string) {
  setLocale(locale as 'zh-CN' | 'en-US')
}

/**
 * 切换主题
 */
function handleThemeChange(theme: string) {
  setTheme(theme as 'light' | 'dark' | 'auto')
}
</script>

<template>
  <div class="app-header">
    <div class="header-left">
      <a-tooltip :title="navigationExpanded ? '收起导航' : '展开导航'">
        <a-button
          type="text"
          class="navigation-toggle"
          :aria-label="navigationExpanded ? '收起导航' : '展开导航'"
          :aria-expanded="navigationExpanded"
          @click="$emit('toggle-navigation')"
        >
          <MenuFoldOutlined v-if="navigationExpanded" />
          <MenuUnfoldOutlined v-else />
        </a-button>
      </a-tooltip>
      <nav class="page-location" aria-label="当前位置">
        <router-link to="/dashboard">工作台</router-link>
        <RightOutlined aria-hidden="true" />
        <span class="current-page" :title="pageTitle" aria-current="page">{{ pageTitle }}</span>
      </nav>
    </div>

    <div class="header-right">
      <!-- 系统提示 -->
      <a-popover placement="bottomRight" trigger="click">
        <template #content>
          <div class="system-notification-content">
            <p>暂无新消息</p>
          </div>
        </template>
        <template #title>
          <span>系统提示</span>
        </template>
        <a-button type="text" class="header-btn" title="系统提示" aria-label="系统提示">
          <BellOutlined />
        </a-button>
      </a-popover>

      <!-- 语言选择 -->
      <a-dropdown class="locale-dropdown">
        <a-button type="text" class="header-btn" :title="localeName" :aria-label="localeName">
          <GlobalOutlined />
        </a-button>

        <template #overlay>
          <a-menu @click="({ key }: { key: string }) => handleLocaleChange(key)">
            <a-menu-item
              v-for="locale in availableLocales"
              :key="locale"
              :class="{ 'ant-menu-item-selected': currentLocale === locale }"
            >
              {{ localeNames[locale] }}
            </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>

      <!-- 主题切换 -->
      <a-dropdown class="theme-dropdown">
        <a-button type="text" class="header-btn" :title="themeText" :aria-label="themeText">
          <component :is="themeIcon" />
        </a-button>

        <template #overlay>
          <a-menu @click="({ key }: { key: string }) => handleThemeChange(key)">
            <a-menu-item
              key="light"
              :class="{ 'ant-menu-item-selected': themeMode === 'light' }"
            >
              <BgColorsOutlined />
              <span class="menu-text">{{ t('component.settings.theme.light') }}</span>
            </a-menu-item>
            <a-menu-item
              key="dark"
              :class="{ 'ant-menu-item-selected': themeMode === 'dark' }"
            >
              <BulbOutlined />
              <span class="menu-text">{{ t('component.settings.theme.dark') }}</span>
            </a-menu-item>
            <a-menu-item
              key="auto"
              :class="{ 'ant-menu-item-selected': themeMode === 'auto' }"
            >
              <DesktopOutlined />
              <span class="menu-text">{{ t('component.settings.theme.auto') }}</span>
            </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>

      <!-- 用户信息下拉菜单 -->
      <a-dropdown class="user-dropdown">
        <button type="button" class="user-info" :aria-label="user?.name || t('common.user')">
          <a-avatar
            :src="avatarUrl"
            size="small"
            class="user-avatar"
          >
            <template #icon>
              <UserOutlined />
            </template>
          </a-avatar>
          <span class="user-name">{{ user?.name || t('common.user') }}</span>
        </button>

        <template #overlay>
          <a-menu>
            <a-menu-item @click="() => goToProfile()">
              <UserOutlined />
              <span class="menu-text">{{ t('common.personalCenter') }}</span>
            </a-menu-item>
            <a-menu-divider />
            <a-menu-item @click="() => handleLogout()">
              <LogoutOutlined />
              <span class="menu-text">{{ t('common.logout') }}</span>
            </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
  height: 56px;
  padding: 0 24px 0 12px;
  gap: 16px;
}

.header-left {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.navigation-toggle { width: 36px; height: 36px; flex: none; }
.page-location { display: flex; align-items: center; gap: 10px; min-width: 0; font-size: 13px; }
.page-location a { color: var(--color-text-secondary); white-space: nowrap; }
.page-location > .anticon { color: var(--color-text-tertiary); font-size: 10px; }
.current-page { color: var(--text-color); overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }

.header-right {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 8px;
  flex-shrink: 0;

  .header-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 6px;
    transition: background-color 0.15s;

    &:hover {
      background-color: var(--color-bg-hover);
    }

    .btn-text {
      font-size: 14px;
      color: var(--text-color);
    }
  }

  .locale-dropdown,
  .theme-dropdown {
    .header-btn {
      min-width: 36px;
    }
  }

  /* Dropdown 会把 class 透传到触发按钮，不能依赖不存在的外层节点。 */
  .user-info {
    cursor: pointer;
    padding: 0 8px;
    margin-left: 4px;
    border: 0;
    background: transparent;
    color: var(--color-text);
    font: inherit;
    border-radius: 6px;
    height: 36px;
    gap: 8px;
    display: flex;
    align-items: center;

    &:hover {
      background: var(--color-bg-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .user-name {
      max-width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.menu-text {
  margin-left: 8px;
  color: var(--color-text-secondary);
}

@media (max-width: 720px) {
  .app-header {
    padding: 0 8px;
  }

  .header-left {
    gap: 6px;
  }

  .header-right {
    justify-content: flex-end;
    gap: 2px;

    .header-btn {
      padding: 0 8px;

      .btn-text {
        display: none;
      }
    }

    .locale-dropdown,
    .theme-dropdown {
      .header-btn {
        min-width: 32px;
      }
    }

    .user-info {
      gap: 0;
      padding: 0 6px;
      margin-left: 0;

      .user-name {
        display: none;
      }
    }
  }
}

@media (max-width: 540px) {
  .app-header { gap: 4px; }
  .page-location > a, .page-location > .anticon { display: none; }
  .header-right .user-name { display: none; }
}
</style>
